// v1.0.0 - 2026-01-27 - GSE Riepilogo sheet parser

import type * as XLSX from 'xlsx'
import type { GSERawRiepilogo, ParseResult } from './gse-types'
import { GSE_COLUMN_MAPPINGS } from './gse-types'
import { sheetToJson, getRowValue, parseNumber, parsePeriod } from './xlsx-utils'
import { validateRiepilogo } from './validators'

/**
 * Parse GSE Riepilogo sheet
 *
 * Expected columns:
 * - POD: Punto di connessione
 * - Tipo: IMMESSA o PRELEVATA
 * - Energia: Totale kWh
 * - Periodo: YYYYMM o MM/YYYY
 */
export function parseRiepilogoSheet(
  sheet: XLSX.WorkSheet,
  periodo?: string
): ParseResult<GSERawRiepilogo[]> {
  const errors: string[] = []
  const warnings: string[] = []
  const data: GSERawRiepilogo[] = []
  let skippedRows = 0

  // Convert sheet to JSON
  const rows = sheetToJson<Record<string, unknown>>(sheet)

  if (rows.length === 0) {
    return {
      success: false,
      data: null,
      errors: ['Foglio vuoto o formato non riconosciuto'],
      warnings: [],
      rowCount: 0,
      skippedRows: 0,
    }
  }

  const mapping = GSE_COLUMN_MAPPINGS.RIEPILOGO

  for (let i = 0; i < rows.length; i++) {
    const row = rows[i]
    const rowNum = i + 2 // Excel rows start at 1, plus header

    // Extract values
    const pod = getRowValue<string>(row, mapping.pod)
    const tipoRaw = getRowValue<string>(row, mapping.tipo)
    const energiaRaw = getRowValue<string | number>(row, mapping.energia)
    const periodoRaw = getRowValue<string>(row, mapping.periodo) || periodo

    // Skip empty rows
    if (!pod && !tipoRaw && !energiaRaw) {
      skippedRows++
      continue
    }

    // Parse values
    const energia = parseNumber(energiaRaw)

    // Normalize tipo
    let tipo: 'IMMESSA' | 'PRELEVATA' | undefined
    if (tipoRaw) {
      const normalized = String(tipoRaw).toUpperCase().trim()
      if (normalized.includes('IMMESSA') || normalized === 'I') {
        tipo = 'IMMESSA'
      } else if (normalized.includes('PRELEVATA') || normalized === 'P') {
        tipo = 'PRELEVATA'
      }
    }

    // Parse periodo
    const parsedPeriodo = parsePeriod(periodoRaw)

    // Build record
    const record: Partial<GSERawRiepilogo> = {
      codice_contratto: '', // Will be set later from context
      pod: pod?.trim(),
      tipo,
      energia_totale_kwh: energia ?? 0,
      periodo: parsedPeriodo || '',
    }

    // Validate
    const validation = validateRiepilogo(record)

    if (!validation.valid) {
      errors.push(`Riga ${rowNum}: ${validation.errors.join(', ')}`)
      skippedRows++
      continue
    }

    if (validation.warnings.length > 0) {
      warnings.push(`Riga ${rowNum}: ${validation.warnings.join(', ')}`)
    }

    data.push(record as GSERawRiepilogo)
  }

  return {
    success: errors.length === 0 || data.length > 0,
    data: data.length > 0 ? data : null,
    errors,
    warnings,
    rowCount: rows.length,
    skippedRows,
  }
}

/**
 * Aggregate riepilogo data by POD
 */
export function aggregateRiepilogoByPOD(
  data: GSERawRiepilogo[]
): Map<string, { immessa: number; prelevata: number }> {
  const result = new Map<string, { immessa: number; prelevata: number }>()

  for (const record of data) {
    const existing = result.get(record.pod) || { immessa: 0, prelevata: 0 }

    if (record.tipo === 'IMMESSA') {
      existing.immessa += record.energia_totale_kwh
    } else {
      existing.prelevata += record.energia_totale_kwh
    }

    result.set(record.pod, existing)
  }

  return result
}

/**
 * Extract unique PODs from riepilogo
 */
export function extractPODs(data: GSERawRiepilogo[]): string[] {
  return [...new Set(data.map((r) => r.pod))]
}

/**
 * Get totals from riepilogo
 */
export function getRiepilogoTotals(data: GSERawRiepilogo[]): {
  immessa_totale: number
  prelevata_totale: number
  pod_count: number
} {
  let immessa_totale = 0
  let prelevata_totale = 0
  const pods = new Set<string>()

  for (const record of data) {
    pods.add(record.pod)
    if (record.tipo === 'IMMESSA') {
      immessa_totale += record.energia_totale_kwh
    } else {
      prelevata_totale += record.energia_totale_kwh
    }
  }

  return {
    immessa_totale,
    prelevata_totale,
    pod_count: pods.size,
  }
}
